import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Download,
  Play,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  Database,
  Mail,
  ShieldOff,
  FileText,
  Clock,
  BarChart3,
  AlertTriangle,
  Zap,
  Server,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STEP_LABELS = {
  starting:                 'Başlatılıyor...',
  scanning_email_events:    'Email geçmişi taranıyor (email_events)...',
  email_events_done:        'Email taraması tamamlandı',
  loading_mongo_suppression:'Suppression listesi yükleniyor (MongoDB)...',
  mongo_suppression_done:   'Suppression listesi yüklendi',
  computing_clean_list:     'Temiz liste hesaplanıyor...',
  writing_exports:          'CSV dosyaları yazılıyor...',
  done:                     'Tamamlandı',
};

const EXPORT_STEPS = [
  { key: 1, label: '1. Email Tarama' },
  { key: 2, label: '2. Suppression (DB)' },
  { key: 3, label: '3. Temiz Liste' },
  { key: 4, label: '4. CSV Export' },
];

function formatSyncStatus(s) {
  const map = {
    never:     { label: 'Hiç senkronize edilmedi', cls: 'text-muted-foreground' },
    running:   { label: 'Çalışıyor...',             cls: 'text-amber-500' },
    completed: { label: 'Tamamlandı',               cls: 'text-emerald-500' },
    failed:    { label: 'Hata',                     cls: 'text-red-500' },
  };
  return map[s] || map.never;
}

function StatCard({ icon: Icon, label, value, color = 'text-muted-foreground', sub }) {
  return (
    <div className="flex flex-col gap-1 p-4 rounded-lg bg-muted/30 border border-border">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className={`text-xl font-bold font-heading ${color}`}>
        {typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
      </p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDT(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR'); } catch { return iso; }
}

export default function EmailExportPage() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);
  const syncPollRef = useRef(null);
  const activeJobIdRef = useRef(null);   // tracks which job is being polled (avoids stale closure)
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Load existing jobs + sync status on mount (NO auto SendGrid sync triggered)
  useEffect(() => {
    loadJobs();
    loadSyncStatus();
  }, []);

  const loadSyncStatus = async () => {
    try {
      const res = await api.get('/sendgrid/sync-status');
      setSyncStatus(res.data);
      if (res.data?.status === 'running' && !syncPollRef.current) {
        startSyncPolling();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const startSyncPolling = () => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    syncPollRef.current = setInterval(async () => {
      try {
        const res = await api.get('/sendgrid/sync-status');
        setSyncStatus(res.data);
        if (res.data?.status !== 'running') {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncing(false);
          if (res.data?.status === 'completed') {
            toast.success(`SendGrid sync tamamlandı — ${(res.data.added || 0).toLocaleString('tr-TR')} yeni eklendi`);
          } else if (res.data?.status === 'failed') {
            toast.error(`SendGrid sync hatası: ${res.data.error || 'bilinmeyen'}`);
          }
        }
      } catch (e) { console.error(e); }
    }, 3000);
  };

  const handleSync = async () => {
    const ok = window.confirm(
      'Bu işlem SendGrid suppression listesini günceller. Büyük listelerde birkaç dakika sürebilir. Devam edilsin mi?'
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const res = await api.post('/sendgrid/sync-suppressions');
      if (res.data?.status === 'already_running') {
        toast.info(res.data.message || 'SendGrid sync zaten çalışıyor.');
      } else {
        toast.success('SendGrid sync başlatıldı');
      }
      setSyncStatus(prev => ({ ...(prev || {}), status: 'running' }));
      startSyncPolling();
    } catch (e) {
      setSyncing(false);
      toast.error(e.response?.data?.detail || 'Sync başlatılamadı');
    }
  };

  const loadJobs = async () => {
    try {
      const res = await api.get('/admin/email-exports/jobs');
      const jobList = res.data || [];
      setJobs(jobList);
      // Auto-resume polling — use ref to avoid stale closure
      if (!activeJobIdRef.current) {
        const running = jobList.find(j => j.status === 'running');
        if (running) {
          activeJobIdRef.current = running.job_id;
          setActiveJob(running);
          startPolling(running.job_id);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    activeJobIdRef.current = jobId;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/admin/email-exports/status/${jobId}`);
        const job = res.data;
        setActiveJob(job);
        setJobs(prev => {
          const idx = prev.findIndex(j => j.job_id === jobId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...job };
            return updated;
          }
          return [job, ...prev];
        });
        if (job.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          activeJobIdRef.current = null;
          // Reload full job data (includes stats) and update activeJob
          try {
            const listRes = await api.get('/admin/email-exports/jobs');
            const jobList = listRes.data || [];
            setJobs(jobList);
            const completed = jobList.find(j => j.job_id === jobId);
            if (completed) setActiveJob(completed);
          } catch (_) { loadJobs(); }
        }
      } catch (e) {
        console.error(e);
      }
    }, 2000);
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (syncPollRef.current) clearInterval(syncPollRef.current);
  }, []);

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await api.post('/admin/email-exports/start');
      const job = res.data;
      if (job.status === 'already_running') {
        toast.info(job.message || 'Zaten çalışan bir export var.');
        // Resume polling the existing job
        if (job.job_id && !activeJobIdRef.current) {
          const statusRes = await api.get(`/admin/email-exports/status/${job.job_id}`);
          setActiveJob(statusRes.data);
          startPolling(job.job_id);
        }
        return;
      }
      activeJobIdRef.current = job.job_id;
      setActiveJob({ ...job, progress: 0, step: 'starting', emails_scanned: 0,
        sg_suppression_count: 0, mongo_suppression_count: 0, clean_count: 0,
        files_ready: false, started_at: new Date().toISOString() });
      toast.success('Export job başlatıldı');
      startPolling(job.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Job başlatılamadı');
    } finally {
      setStarting(false);
    }
  };

  const handleDownload = (jobId, fileType) => {
    const url = `${API_BASE}/api/admin/email-exports/download/${jobId}/${fileType}?token=${token}`;
    window.open(url, '_blank');
  };

  const currentJob = activeJob
    || (activeJobIdRef.current && jobs.find(j => j.job_id === activeJobIdRef.current))
    || jobs[0];
  const isRunning = currentJob?.status === 'running';
  const isDone = currentJob?.status === 'completed';
  const isFailed = currentJob?.status === 'failed';
  const lastCompleted = jobs.find(j => j.status === 'completed') || null;

  const stepLabel = STEP_LABELS[currentJob?.step] || currentJob?.step || '—';

  const stats = currentJob?.stats || {};

  return (
    <div className="space-y-6" data-testid="email-export-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Email Export Merkezi</h1>
          <p className="text-muted-foreground mt-1">
            Production email geçmişi ve SendGrid suppression tam dışa aktarma
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={loadJobs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
          <Button
            onClick={handleStart}
            disabled={starting || isRunning}
            data-testid="start-export-btn"
            size="lg"
          >
            {starting || isRunning ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Play className="w-5 h-5 mr-2" />
            )}
            {isRunning ? 'Çalışıyor...' : 'Tam Export Başlat'}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
        <Zap className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-emerald-500">Hızlı Export</p>
          <p className="text-muted-foreground mt-0.5">
            Export artık SendGrid'i baştan çekmez; mevcut MongoDB suppression listesini kullanır ve
            genellikle <strong>saniyeler içinde</strong> tamamlanır. Suppression listesini güncellemek için
            aşağıdaki <strong>"SendGrid Senkronize Et"</strong> butonunu manuel olarak kullanın.
          </p>
        </div>
      </div>

      {/* SendGrid Status Card */}
      <Card className="border-border" data-testid="sendgrid-status-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="w-4 h-4 text-primary" />
              SendGrid Suppression Durumu
            </CardTitle>
            <Button
              onClick={handleSync}
              disabled={syncing || syncStatus?.status === 'running'}
              size="sm"
              variant="outline"
              data-testid="sendgrid-sync-btn"
            >
              {(syncing || syncStatus?.status === 'running')
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              {(syncing || syncStatus?.status === 'running') ? 'Senkronize ediliyor...' : 'SendGrid Senkronize Et'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                {syncStatus?.sg_connected
                  ? <><Wifi className="w-3 h-3 text-emerald-500" /> Bağlantı</>
                  : <><WifiOff className="w-3 h-3 text-red-500" /> Bağlantı</>}
              </p>
              <p className={`text-sm font-medium ${syncStatus?.sg_connected ? 'text-emerald-500' : 'text-red-500'}`}>
                {syncStatus?.sg_connected ? 'Bağlı' : 'API Key Yok'}
              </p>
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Toplam Suppression</p>
              <p className="text-sm font-bold" data-testid="suppression-total">
                {(syncStatus?.current_suppression_count ?? 0).toLocaleString('tr-TR')}
              </p>
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Son Sync Durumu</p>
              <p className={`text-sm font-medium ${formatSyncStatus(syncStatus?.status).cls}`}>
                {formatSyncStatus(syncStatus?.status).label}
              </p>
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Son Sync Zamanı</p>
              <p className="text-sm">{formatDT(syncStatus?.completed_at)}</p>
            </div>
          </div>
          {syncStatus?.status === 'completed' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border text-xs">
              <div><span className="text-muted-foreground">Alınan: </span><span className="font-medium">{(syncStatus.total_fetched ?? 0).toLocaleString('tr-TR')}</span></div>
              <div><span className="text-muted-foreground">Yeni: </span><span className="font-medium text-emerald-500">{(syncStatus.added ?? 0).toLocaleString('tr-TR')}</span></div>
              <div><span className="text-muted-foreground">Güncellenen/Atlanan: </span><span className="font-medium">{(syncStatus.updated ?? 0).toLocaleString('tr-TR')}</span></div>
              <div><span className="text-muted-foreground">Süre: </span><span className="font-medium">{syncStatus.duration_sec ?? 0} sn</span></div>
            </div>
          )}
          {syncStatus?.status === 'failed' && syncStatus?.error && (
            <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-500">
              Hata: {syncStatus.error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Last Export Summary Card */}
      <Card className="border-border" data-testid="last-export-summary">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Son Export Özeti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!lastCompleted ? (
            <p className="text-sm text-muted-foreground py-2">Henüz tamamlanmış export yok.</p>
          ) : (() => {
            const lc = lastCompleted;
            const s = lc.stats || {};
            const durationMs = lc.completed_at && lc.started_at
              ? new Date(lc.completed_at) - new Date(lc.started_at)
              : null;
            const statusCfg = {
              completed: { label: 'Tamamlandı', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: CheckCircle },
              running:   { label: 'Çalışıyor',  cls: 'bg-amber-500/10  text-amber-500  border-amber-500/20',  icon: Loader2 },
              failed:    { label: 'Hata',        cls: 'bg-red-500/10    text-red-500    border-red-500/20',    icon: XCircle },
            };
            const sc = statusCfg[lc.status] || statusCfg.completed;
            const StatusIcon = sc.icon;
            return (
              <div className="space-y-4">
                {/* Meta row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Job ID</p>
                    <p className="text-xs font-mono truncate" title={lc.job_id}>{lc.job_id}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Durum</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${sc.cls}`}>
                      <StatusIcon className="w-3 h-3" />
                      {sc.label}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Başlangıç</p>
                    <p className="text-xs">{formatDT(lc.started_at)}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Bitiş</p>
                    <p className="text-xs">{formatDT(lc.completed_at)}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Süre</p>
                    <p className="text-xs font-mono font-medium">{formatDuration(durationMs)}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Clean Rate</p>
                    <p className="text-xs font-medium text-emerald-500">%{s.clean_rate_pct ?? '—'}</p>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 pt-1 border-t border-border">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold font-heading text-blue-500">
                      {(s.master_unique_emails ?? lc.emails_scanned ?? 0).toLocaleString('tr-TR')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Master Emails</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold font-heading text-amber-500">
                      {(s.sg_suppression_count ?? lc.sg_suppression_count ?? 0).toLocaleString('tr-TR')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Suppression Count</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-2xl font-bold font-heading text-emerald-500">
                      {(s.final_clean_count ?? lc.clean_count ?? 0).toLocaleString('tr-TR')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Final Clean Emails</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Active Job Progress */}
      {currentJob && (
        <Card className={`border ${isFailed ? 'border-destructive/40' : isDone ? 'border-emerald-500/40' : 'border-primary/30'}`}
          data-testid="job-progress-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                {isRunning && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
                {isDone && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                {isFailed && <XCircle className="w-5 h-5 text-destructive" />}
                Export Job
                <span className="text-xs font-mono text-muted-foreground ml-2">
                  {currentJob.job_id}
                </span>
              </CardTitle>
              <Badge variant={
                isDone ? 'default' : isFailed ? 'destructive' : 'secondary'
              }>
                {isDone ? 'Tamamlandı' : isFailed ? 'Hata' : 'Çalışıyor'}
              </Badge>
            </div>
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {currentJob.started_at ? new Date(currentJob.started_at).toLocaleString('tr-TR') : '—'}
              {currentJob.completed_at && (
                <span className="ml-2 text-emerald-500">
                  → {new Date(currentJob.completed_at).toLocaleString('tr-TR')}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{stepLabel}</span>
                <span className="font-medium">{currentJob.progress || 0}%</span>
              </div>
              <Progress value={currentJob.progress || 0} className="h-2" />
            </div>

            {/* Step progress */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              <span>Adım {currentJob.current_step || 0} / {currentJob.total_steps || 4}</span>
              {EXPORT_STEPS.map(({ key, label }) => {
                const done = key < (currentJob.current_step || 0) || (isDone && key <= 4);
                const active = key === (currentJob.current_step || 0) && isRunning;
                return (
                  <span key={key} className={`px-2 py-0.5 rounded ${done ? 'bg-emerald-500/20 text-emerald-500' : active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {done ? '✓' : active ? '●' : '○'} {label}
                  </span>
                );
              })}
            </div>

            {/* Live Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                icon={Mail}
                label="Unique Emails"
                value={currentJob.emails_scanned || 0}
                color="text-blue-500"
              />
              <StatCard
                icon={ShieldOff}
                label="Suppression (DB)"
                value={currentJob.mongo_suppression_count || 0}
                color="text-amber-500"
              />
              <StatCard
                icon={CheckCircle}
                label="Final Clean"
                value={currentJob.clean_count || 0}
                color="text-emerald-500"
              />
            </div>

            {/* Error */}
            {isFailed && currentJob.error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive">
                <strong>Hata:</strong> {currentJob.error}
              </div>
            )}

            {/* Completed stats */}
            {isDone && stats && (
              <div className="pt-2 border-t border-border space-y-3">
                <p className="text-sm font-medium">Detaylı İstatistikler</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard icon={BarChart3} label="Toplam Event" value={stats.total_event_count || 0} />
                  <StatCard icon={CheckCircle} label="Processed" value={stats.processed_count || 0} color="text-blue-500" />
                  <StatCard icon={CheckCircle} label="Delivered" value={stats.delivered_count || 0} color="text-emerald-500" />
                  <StatCard icon={XCircle} label="Bounced" value={stats.bounce_count || 0} color="text-red-500" />
                  <StatCard icon={BarChart3} label="Deferred" value={stats.deferred_count || 0} />
                  <StatCard icon={BarChart3} label="Dropped" value={stats.dropped_count || 0} />
                  <StatCard icon={Mail} label="Opened" value={stats.open_count || 0} color="text-violet-500" />
                  <StatCard icon={BarChart3} label="Clicked" value={stats.click_count || 0} color="text-violet-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-center">
                    <p className="text-3xl font-bold text-emerald-500">
                      {(stats.final_clean_count || 0).toLocaleString('tr-TR')}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">Final Temiz Email</p>
                    <p className="text-xs text-emerald-500 mt-0.5">%{stats.clean_rate_pct} clean rate</p>
                  </div>
                  <div className="p-4 rounded-lg border border-border bg-muted/30 space-y-1.5">
                    <p className="text-xs text-muted-foreground">File sizes:</p>
                    {Object.entries(stats.file_sizes || {}).map(([k, v]) => (
                      <p key={k} className="text-xs">
                        <span className="font-mono">{k.replace('_mb', '.csv')}</span>
                        <span className="text-muted-foreground ml-2">{v} MB</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Download Buttons */}
            {isDone && currentJob.files_ready && (
              <div className="pt-2 border-t border-border">
                <p className="text-sm font-medium mb-3">CSV Dosyaları İndir</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { type: 'master',       label: 'master_emails.csv',        color: 'text-blue-500',    desc: `${(stats.master_unique_emails || 0).toLocaleString()} satır` },
                    { type: 'suppressions', label: 'sg_suppressions.csv',      color: 'text-amber-500',   desc: `${(stats.sg_suppression_count || 0).toLocaleString()} satır` },
                    { type: 'clean',        label: 'final_clean_emails.csv',   color: 'text-emerald-500', desc: `${(stats.final_clean_count || 0).toLocaleString()} satır` },
                    { type: 'stats',        label: 'stats.json',               color: 'text-violet-500',  desc: 'Full statistics' },
                  ].map(({ type, label, color, desc }) => (
                    <button
                      key={type}
                      onClick={() => handleDownload(currentJob.job_id, type)}
                      className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-center"
                      data-testid={`download-${type}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <FileText className={`w-5 h-5 ${color}`} />
                      </div>
                      <div>
                        <p className="text-xs font-medium font-mono">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                      <Download className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previous Jobs */}
      {jobs.length > 1 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base">Önceki Export İşlemleri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {jobs.slice(1, 10).map(j => (
                <div key={j.job_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setActiveJob(j)}
                >
                  <div className="flex items-center gap-3">
                    {j.status === 'completed' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                    {j.status === 'failed' && <XCircle className="w-4 h-4 text-destructive" />}
                    {j.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                    <div>
                      <p className="text-sm font-mono">{j.job_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {j.started_at ? new Date(j.started_at).toLocaleString('tr-TR') : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {j.emails_scanned > 0 && <span>{j.emails_scanned.toLocaleString()} email</span>}
                    {j.clean_count > 0 && <span className="text-emerald-500">{j.clean_count.toLocaleString()} clean</span>}
                    {j.files_ready && (
                      <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); handleDownload(j.job_id, 'clean'); }}>
                        <Download className="w-3 h-3 mr-1" /> Clean
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {jobs.length === 0 && !starting && (
        <Card className="border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Database className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">Henüz export çalıştırılmadı</p>
            <p className="text-sm text-muted-foreground mt-1">
              Production'da "Tam Export Başlat" butonuna basın
            </p>
            <Button className="mt-6" onClick={handleStart}>
              <Play className="w-4 h-4 mr-2" />
              Tam Export Başlat
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
